"use client"

import { startTransition, useOptimistic } from "react"

import {
    type SearchFilter,
    type Values,
    type ValuesFilterMeta
} from "@/lib/depict-client/types/api/ProductListingRequestV3"
import { FOCUS_VISIBLE_BORDER_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"

type FilterCheckboxProps = {
    item: Values
    field: string
    op: "eq" | "neq" | "in" | "nin" | "leq" | "geq" | "inrange"
    isSelected: boolean
    isSizeFilter: boolean
    showColorSwatch?: boolean
    valueIndex: number
    filter: SearchFilter
    // Explicit count override. The size filter aggregates counts across
    // multiple Depict `meta.values` entries (many Centra sizes map to a single
    // general size bucket), so `meta.counts[valueIndex]` is not meaningful
    // there. When provided, this value takes precedence over the per-index
    // lookup.
    countOverride?: number
    onAddFilter: (field: string, op: SearchFilter["op"], value: string) => void
}

const WHITE_SWATCH_COLORS = ["#fafafa", "#ffffff", "white"]

const FilterCheckbox = ({
    item,
    field,
    op,
    isSelected,
    valueIndex,
    isSizeFilter,
    showColorSwatch = false,
    filter,
    countOverride,
    onAddFilter
}: FilterCheckboxProps) => {
    const meta = filter.meta as ValuesFilterMeta | undefined
    const colorHex = showColorSwatch
        ? (meta?.swatches?.[valueIndex] ?? WHITE_SWATCH_COLORS[0])
        : undefined

    const isWhiteSwatch = WHITE_SWATCH_COLORS.includes(colorHex?.toLowerCase() ?? "")

    const itemLabel = isSizeFilter ? item : meta?.names?.[valueIndex] || ""
    const count = countOverride ?? meta?.counts?.[valueIndex] ?? 0
    const isClickable = count > 0
    const [optimisticSelected, setOptimisticSelected] = useOptimistic(isSelected)

    const handleClick = () => {
        if (!isClickable) return
        startTransition(() => {
            setOptimisticSelected(!optimisticSelected)
            onAddFilter(field ?? "", op as SearchFilter["op"], String(item))
        })
    }

    return (
        <button
            type="button"
            data-checked={optimisticSelected}
            aria-pressed={optimisticSelected}
            disabled={!isClickable}
            onClick={handleClick}
            className={clsx(
                "relative -mt-px flex h-44 w-full items-center gap-12 border border-col-border-neutral pr-12 text-left first:mt-0",
                "transition-[border-color] duration-200 ease-in-out",
                "lg:hover:z-10 lg:hover:border-col-border-subtle",
                "data-[checked=true]:bg-col-bg-calm",
                FOCUS_VISIBLE_BORDER_CLASS,
                !showColorSwatch && "pl-12",
                isClickable ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            )}
        >
            {colorHex && (
                <div
                    className={clsx(
                        "w-16 shrink-0 self-stretch",
                        isWhiteSwatch && "border-r border-col-border-neutral"
                    )}
                    style={{ backgroundColor: colorHex }}
                    aria-hidden="true"
                />
            )}
            <span className="pt-3 capitalize Trade_Gothic_Text_14_150_0_300">
                {itemLabel}
            </span>
        </button>
    )
}

export default FilterCheckbox
